"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { motion, useReducedMotion } from "framer-motion";
import {
  BadgeCheck,
  CalendarClock,
  Check,
  Mic,
  Search,
  ShieldCheck,
} from "lucide-react";
import LemonMark from "@/components/ui/LemonMark";

/* Hero feature views — each one is a PRODUCT MOMENT starring one feature
   (voice, models + second opinion, PII masking, integrations, the store),
   not a page tour. They slot into ChatAppPreview's rotating frame exactly
   like DashboardAppPreview / WorkflowAppPreview: an aspect-locked surface
   that fills the persistent window chrome. Frames with a real capture in
   public/images/product/ show that as a close-up card; the rest are coded
   mocks. Either way every name, address and number is fictional, shot in
   a staged demo workspace (#6774: nothing posing as a real customer
   artifact). */

function useMockTheme(glass: boolean) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = mounted && resolvedTheme === "dark";

  const c = isDark
    ? {
        glassBg: "rgba(13,13,14,0.55)",
        glassBorder: "rgba(255,255,255,0.1)",
        text: "rgba(255,255,255,0.92)",
        subText: "rgba(255,255,255,0.45)",
        faint: "rgba(255,255,255,0.3)",
        cardBg: "#1b1b1b",
        cardBorder: "rgba(255,255,255,0.08)",
        bubbleUserBg: "rgba(224,167,48,0.14)",
        bubbleBg: "#1b1b1b",
        composerBg: "#151515",
        pillBorder: "rgba(255,255,255,0.14)",
        gold: "#e0a730",
        goldSoftBg: "rgba(224,167,48,0.12)",
        goldSoftBorder: "rgba(224,167,48,0.4)",
        badgeBg: "#FFE500",
        badgeText: "#161616",
      }
    : {
        glassBg: "rgba(255,255,255,0.55)",
        glassBorder: "rgba(255,255,255,0.7)",
        text: "rgba(0,0,0,0.85)",
        subText: "rgba(0,0,0,0.5)",
        faint: "rgba(0,0,0,0.35)",
        cardBg: "#ffffff",
        cardBorder: "rgba(0,0,0,0.07)",
        bubbleUserBg: "rgba(224,167,48,0.16)",
        bubbleBg: "#ffffff",
        composerBg: "#fffdf9",
        pillBorder: "rgba(0,0,0,0.12)",
        gold: "#c9820a",
        goldSoftBg: "rgba(224,167,48,0.14)",
        goldSoftBorder: "rgba(201,130,10,0.45)",
        badgeBg: "#FFE500",
        badgeText: "#161616",
      };

  return { c, isDark, glassActive: glass };
}

function ViewShell({
  glass,
  glassBg,
  glassBorder,
  children,
  center = false,
}: {
  glass: boolean;
  glassBg: string;
  glassBorder: string;
  children: React.ReactNode;
  center?: boolean;
}) {
  return (
    <div className="relative select-none text-left" aria-hidden="true">
      <div
        className={`flex flex-col aspect-[3/4] md:aspect-[2980/1894] overflow-hidden px-3 py-5 sm:px-6 sm:py-6 md:px-10 md:py-8 ${
          center ? "items-center justify-center" : ""
        } ${glass ? "hero-glass-surface" : "rounded-2xl"}`}
        style={
          glass
            ? undefined
            : {
                background: glassBg,
                borderTop: `1px solid ${glassBorder}`,
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
              }
        }
      >
        {children}
      </div>
    </div>
  );
}

function TitleBar({
  title,
  sub,
  c,
}: {
  title: string;
  sub: string;
  c: ReturnType<typeof useMockTheme>["c"];
}) {
  return (
    <div className="flex w-full shrink-0 items-center gap-3">
      <LemonMark size={18} />
      <div className="leading-tight">
        <p className="text-[13px] font-medium" style={{ color: c.text }}>
          {title}
        </p>
        <p className="text-[12px]" style={{ color: c.subText }}>
          {sub}
        </p>
      </div>
    </div>
  );
}

/* A real product capture, presented as a clean close-up card. */
function ShotCard({
  src,
  width,
  height,
  cardBorder,
  mobileCrop,
}: {
  src: string;
  width: number;
  height: number;
  cardBorder: string;
  /** Phones only: show the shot at this height, cropped to `focus`, so a
      wide capture stays legible instead of shrinking to fit ~340px. */
  mobileCrop?: { height: number; focus: string };
}) {
  return (
    <div
      className={`w-full overflow-hidden rounded-2xl ${
        mobileCrop ? "max-md:[&_img]:h-[var(--shot-h)] max-md:[&_img]:object-cover max-md:[&_img]:[object-position:var(--shot-focus)]" : ""
      }`}
      style={{
        border: `1px solid ${cardBorder}`,
        background: "#fffdf9",
        boxShadow: "0 18px 50px -18px rgba(0,0,0,0.35)",
        ...(mobileCrop && {
          "--shot-h": `${mobileCrop.height}px`,
          "--shot-focus": mobileCrop.focus,
        }),
      } as React.CSSProperties}
    >
      <Image
        src={src}
        width={width}
        height={height}
        alt=""
        className="h-auto w-full"
        sizes="(max-width: 768px) 88vw, 640px"
      />
    </div>
  );
}

/* ── Voice ───────────────────────────────────────────────────────────── */

export function VoiceView({ glass = false }: { glass?: boolean }) {
  const { c, glassActive } = useMockTheme(glass);
  const reduce = useReducedMotion();
  const bars = [16, 30, 44, 34, 52, 40, 58, 46, 60, 42, 50, 32, 44, 28, 36, 18];
  return (
    <ViewShell glass={glassActive} glassBg={c.glassBg} glassBorder={c.glassBorder}>
      <TitleBar title="Lead finder" sub="Voice conversation" c={c} />
      <div className="flex w-full flex-1 flex-col items-center justify-center gap-6 md:gap-8">
        <motion.span
          className="flex h-20 w-20 items-center justify-center rounded-full md:h-24 md:w-24"
          style={{
            background: c.badgeBg,
            color: c.badgeText,
            boxShadow: "0 12px 50px -8px rgba(224,167,48,0.6)",
          }}
          animate={reduce ? undefined : { scale: [1, 1.06, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <Mic size={32} strokeWidth={2} />
        </motion.span>
        <div className="flex h-16 items-center gap-[4px]">
          {bars.map((h, i) => (
            <motion.span
              key={i}
              className="w-[5px] rounded-full"
              style={{ height: h, background: c.gold }}
              animate={reduce ? undefined : { scaleY: [1, 0.4, 1] }}
              transition={{
                duration: 1,
                repeat: Infinity,
                delay: i * 0.07,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
        <div className="flex max-w-md flex-col items-center gap-2 text-center">
          <p className="text-[14.5px] leading-snug" style={{ color: c.text }}>
            “What changed in the pipeline this week?”
          </p>
          <p className="text-[13px] leading-snug" style={{ color: c.subText }}>
            Answering out loud: three new qualified leads, one gone quiet…
          </p>
        </div>
      </div>
    </ViewShell>
  );
}

/* ── Models: the real picker, mid-chat ───────────────────────────────── */

export function ModelsView({ glass = false }: { glass?: boolean }) {
  const { c, glassActive } = useMockTheme(glass);
  return (
    <ViewShell glass={glassActive} glassBg={c.glassBg} glassBorder={c.glassBorder}>
      <TitleBar title="Chat" sub="The model picker, straight from the app" c={c} />
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-3 py-2">
        <ShotCard
          src="/images/product/model-picker-gemini.png"
          width={703}
          height={530}
          cardBorder={c.cardBorder}
          mobileCrop={{ height: 380, focus: "left top" }}
        />
        <p className="text-[12.5px] leading-snug" style={{ color: c.subText }}>
          Anthropic, OpenAI, Google, DeepSeek, Mistral and more. Switch
          mid-chat, thread kept.
        </p>
      </div>
    </ViewShell>
  );
}

/* ── Privacy: the real PII review panel ──────────────────────────────── */

export function PrivacyView({ glass = false }: { glass?: boolean }) {
  const { c, glassActive } = useMockTheme(glass);
  return (
    <ViewShell glass={glassActive} glassBg={c.glassBg} glassBorder={c.glassBorder}>
      <TitleBar title="Chat" sub="PII masking on" c={c} />
      <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center gap-4 py-2">
        <ShotCard
          src="/images/product/pii-review.png"
          width={712}
          height={263}
          cardBorder={c.cardBorder}
        />
        <div
          className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-[12.5px]"
          style={{
            background: c.goldSoftBg,
            border: `1px solid ${c.goldSoftBorder}`,
            color: c.text,
          }}
        >
          <ShieldCheck size={15} style={{ color: c.gold }} />
          With PII masking on, caught on input and masked before anything reaches the model.
        </div>
      </div>
    </ViewShell>
  );
}

/* ── Integrations: acts in your tools ────────────────────────────────── */

const TOOLS = [
  { name: "Gmail", image: "/images/skill-icons_gmail-light__logo.svg" },
  { name: "Slack", image: "/images/selfhst_slack__logo.svg" },
  { name: "Calendar", image: "/images/logos_google-calendar__logo.svg" },
  { name: "Drive", image: "/images/selfhst_google-drive__logo.svg" },
  { name: "Notion", image: "/images/selfhst_notion__logo.svg" },
  { name: "HubSpot", image: "/images/HubSpot__logo.svg" },
];

const TOOL_ACTIVITY = [
  { icon: TOOLS[0].image, text: "Drafted a reply to Alex, waiting for your review" },
  { icon: TOOLS[2].image, text: "Added Thursday's client call, with a prep brief" },
  { icon: TOOLS[3].image, text: "Filed the Acme research brief in Drive" },
];

export function IntegrationsView({ glass = false }: { glass?: boolean }) {
  const { c, glassActive } = useMockTheme(glass);
  return (
    <ViewShell glass={glassActive} glassBg={c.glassBg} glassBorder={c.glassBorder}>
      <TitleBar title="Integrations" sub="Connected to your workspace" c={c} />
      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center gap-4">
        <div className="grid grid-cols-3 gap-2.5 md:grid-cols-6">
          {TOOLS.map((tool) => (
            <div
              key={tool.name}
              className="flex flex-col items-center gap-1.5 rounded-xl px-2 py-3"
              style={{ background: c.cardBg, border: `1px solid ${c.cardBorder}` }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={tool.image} alt="" className="h-6 w-6" loading="lazy" />
              <span className="text-[10.5px]" style={{ color: c.subText }}>
                {tool.name}
              </span>
              <span
                className="flex items-center gap-1 text-[9.5px] font-medium"
                style={{ color: c.gold }}
              >
                <Check size={9} strokeWidth={3} /> Connected
              </span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-[11px] uppercase tracking-wide" style={{ color: c.faint }}>
            This morning
          </p>
          {TOOL_ACTIVITY.map((row) => (
            <div
              key={row.text}
              className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-[13px]"
              style={{ background: c.cardBg, border: `1px solid ${c.cardBorder}` }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={row.icon} alt="" className="h-4 w-4" loading="lazy" />
              <span style={{ color: c.text }}>{row.text}</span>
            </div>
          ))}
        </div>
      </div>
    </ViewShell>
  );
}

/* ── Canvas dashboard: the real thing — the agent drew this beside the
      chat (Social Media Planner, staged demo, fictional data). Two close-ups:
      the at-a-glance top and the posting-times heatmap. ─────────────────── */

export function CanvasView({ glass = false }: { glass?: boolean }) {
  const { c, glassActive } = useMockTheme(glass);
  return (
    <ViewShell glass={glassActive} glassBg={c.glassBg} glassBorder={c.glassBorder}>
      <TitleBar
        title="Social Media Planner"
        sub="Launch-week dashboard, drawn by the agent beside the chat"
        c={c}
      />
      <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center gap-3 py-2">
        <ShotCard
          src="/images/product/dashboard-launch-week.png"
          width={740}
          height={310}
          cardBorder={c.cardBorder}
        />
        <ShotCard
          src="/images/product/dashboard-heatmap.png"
          width={740}
          height={198}
          cardBorder={c.cardBorder}
        />
        <p className="text-[12.5px] leading-snug" style={{ color: c.subText }}>
          KPIs, projections, a best-time heatmap. Ask about any block, right
          in the chat.
        </p>
      </div>
    </ViewShell>
  );
}

/* ── Agent trace: the real run record — the product's own rationale
      (#3959): the trace instils trust by showing its work. This is the
      actual trace of the dashboard being planned and drawn. ────────────── */

export function TraceView({ glass = false }: { glass?: boolean }) {
  const { c, glassActive } = useMockTheme(glass);
  return (
    <ViewShell glass={glassActive} glassBg={c.glassBg} glassBorder={c.glassBorder}>
      <TitleBar
        title="Social Media Planner: agentic run"
        sub="The trace behind the dashboard"
        c={c}
      />
      <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center gap-3 py-2">
        <ShotCard
          src="/images/product/agent-trace.png"
          width={930}
          height={452}
          cardBorder={c.cardBorder}
          mobileCrop={{ height: 340, focus: "left top" }}
        />
        <p
          className="flex items-center gap-2 text-[12px]"
          style={{ color: c.subText }}
        >
          <ShieldCheck size={13} style={{ color: c.gold }} />
          Every step and tool call logged. Nothing sends without your
          sign-off.
        </p>
      </div>
    </ViewShell>
  );
}

/* ── Scheduled runs: not the workflow list — the "it ran without you"
      moment, results already landed. ─────────────────────────────────── */

export function ScheduleView({ glass = false }: { glass?: boolean }) {
  const { c, glassActive } = useMockTheme(glass);
  const runs = [
    { name: "Morning brief", detail: "In your inbox", time: "07:02" },
    { name: "Inbox digest", detail: "4 need a reply, 11 can wait", time: "07:15" },
    { name: "Weekly look-ahead", detail: "Next run Monday", time: "07:00", upcoming: true },
  ];
  return (
    <ViewShell glass={glassActive} glassBg={c.glassBg} glassBorder={c.glassBorder}>
      <TitleBar title="Scheduled runs" sub="While you slept" c={c} />
      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center gap-2.5">
        {runs.map((run) => (
          <div
            key={run.name}
            className="flex items-center gap-3.5 rounded-2xl px-4 py-3.5"
            style={{
              background: c.cardBg,
              border: `1px solid ${run.upcoming ? c.cardBorder : c.goldSoftBorder}`,
              opacity: run.upcoming ? 0.75 : 1,
            }}
          >
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
              style={
                run.upcoming
                  ? { border: `1px solid ${c.pillBorder}`, color: c.faint }
                  : { background: c.badgeBg, color: c.badgeText }
              }
            >
              {run.upcoming ? (
                <CalendarClock size={13} />
              ) : (
                <Check size={13} strokeWidth={3} />
              )}
            </span>
            <span className="min-w-0">
              <span className="block text-[14px] font-medium" style={{ color: c.text }}>
                {run.name}
              </span>
              <span className="block text-[12px]" style={{ color: c.subText }}>
                {run.detail}
              </span>
            </span>
            <span className="ml-auto text-[12.5px]" style={{ color: c.faint }}>
              {run.time}
            </span>
          </div>
        ))}
        <div
          className="mt-1 rounded-2xl px-4 py-3.5"
          style={{ background: c.bubbleBg, border: `1px solid ${c.cardBorder}` }}
        >
          <p className="text-[11px] uppercase tracking-wide" style={{ color: c.faint }}>
            Morning brief · 07:02
          </p>
          <p className="mt-1.5 text-[13px] leading-relaxed" style={{ color: c.text }}>
            Three things need you today: the Hargreaves reply, sign-off on the
            May invoice run, and prep for Thursday&apos;s client call. Brief
            attached.
          </p>
        </div>
      </div>
    </ViewShell>
  );
}

/* ── Store ───────────────────────────────────────────────────────────── */

const LISTINGS = [
  { title: "Lead finder", by: "Your listing · example", price: "£12/mo", mine: true },
  { title: "Proposal writer", by: "LaunchLemonade template", price: "£9/mo", mine: false },
  { title: "Meeting prep", by: "LaunchLemonade template", price: "Free", mine: false },
  { title: "Deal desk", by: "LaunchLemonade template", price: "£15/mo", mine: false },
];

export function StoreView({ glass = false }: { glass?: boolean }) {
  const { c, glassActive } = useMockTheme(glass);
  return (
    <ViewShell glass={glassActive} glassBg={c.glassBg} glassBorder={c.glassBorder}>
      <div className="flex w-full shrink-0 items-center gap-3">
        <LemonMark size={18} />
        <div className="leading-tight">
          <p className="text-[13px] font-medium" style={{ color: c.text }}>
            Store
          </p>
          <p className="text-[12px]" style={{ color: c.subText }}>
            Assistants people sell, including yours
          </p>
        </div>
        <span
          className="ml-auto flex items-center gap-2 rounded-full px-3 py-1.5 text-[12px]"
          style={{ border: `1px solid ${c.pillBorder}`, color: c.faint }}
        >
          <Search size={12} /> Search the store
        </span>
      </div>
      <div className="mx-auto grid w-full max-w-2xl flex-1 content-center grid-cols-1 gap-2.5 sm:grid-cols-2">
        {LISTINGS.map((item) => (
          <div
            key={item.title}
            className="flex flex-col gap-1.5 rounded-2xl px-4 py-3.5"
            style={{
              background: c.cardBg,
              border: `1px solid ${item.mine ? c.goldSoftBorder : c.cardBorder}`,
            }}
          >
            <div className="flex items-center gap-2">
              <p className="text-[13.5px] font-medium" style={{ color: c.text }}>
                {item.title}
              </p>
              {item.mine && (
                <BadgeCheck size={13} style={{ color: c.gold }} />
              )}
              <span
                className="ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold"
                style={
                  item.mine
                    ? { background: c.badgeBg, color: c.badgeText }
                    : { border: `1px solid ${c.pillBorder}`, color: c.subText }
                }
              >
                {item.price}
              </span>
            </div>
            <p className="text-[11.5px]" style={{ color: c.subText }}>
              {item.by}
            </p>
          </div>
        ))}
      </div>
      <p
        className="mx-auto flex shrink-0 items-center gap-2 pb-1 text-[12px]"
        style={{ color: c.subText }}
      >
        <CalendarClock size={12} style={{ color: c.gold }} />
        Free to publish. You earn when it sells.
      </p>
    </ViewShell>
  );
}
